import {Quiz, QuizQuestion} from '../quiz-api';
import { ConflictHandler, RefetchOptions, MergeOptions } from '../../../graphql/conflictResolution/types';
import {applyMerge} from "../../../graphql/conflictHandlers/applyMerge";

/**
 * Fields that should never be modified by merge (immutable or system-managed)
 */
const IMMUTABLE_FIELDS = ['id', 'ownerIdAndWsId', 'quizId', '__typename'];

/**
 * Fields that should always come from current state (system-managed)
 * CRITICAL: Uses version field (PostgreSQL), not version
 */
const SYSTEM_FIELDS = ['version', 'updatedAt'];

/**
 * Conflict handler for QuizQuestion entities.
 * Handles version conflicts during quiz question updates with intelligent merging.
 * 
 * CRITICAL: QuizQuestion uses version field (PostgreSQL), same as Task tables.
 */
export const quizQuestionConflictHandler: ConflictHandler<QuizQuestion> = {
    entityType: 'QuizQuestion',

    async refetch({ entityId, dataClientService, authService }: RefetchOptions): Promise<QuizQuestion> {
        // Fallback refetch - server should provide currentState
        throw new Error(
            'QuizQuestion refetch not implemented - server should provide currentState in conflict response'
        );
    },

    merge({ currentState, intendedChanges }: MergeOptions<QuizQuestion>): Partial<QuizQuestion> {
        const merged: Partial<QuizQuestion> & Record<string, any> = {};

        // Start with current state
        Object.assign(merged, currentState);

        applyMerge<QuizQuestion>(merged, intendedChanges, IMMUTABLE_FIELDS, SYSTEM_FIELDS);

        // Ensure version is from current state (critical for retry)
        (merged as any).version = (currentState as any).version;

        return merged;
    },

    maxRetries: 3,

    shouldRetry(conflict, attemptNumber): boolean {
        // Don't retry if entity was deleted
        if (conflict.conflictType === 'ENTITY_DELETED') {
            return false;
        }
        // Don't retry beyond max attempts
        return attemptNumber < 3;
    }
};
